import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole, AppRole } from "@/hooks/useUserRole";
import { useStaffAccess } from "@/hooks/useStaffAccess";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: AppRole[];
  requireBusinessOwner?: boolean;
  allowStaff?: boolean;
  blockStaff?: boolean; // NEW: Explicitly block staff from this route
}

export function ProtectedRoute({ 
  children, 
  allowedRoles,
  requireBusinessOwner = false,
  allowStaff = false,
  blockStaff = false // NEW
}: ProtectedRouteProps) {
  const { user, loading: authLoading, isEmailVerified, profile } = useAuth();
  const { role, isLoading: roleLoading, isBusinessOwner } = useUserRole();
  const { isStaff, hasAccess, loading: staffLoading } = useStaffAccess();
  const location = useLocation();

  const isLoading = authLoading || roleLoading || staffLoading;

  // Compute final redirect destination in a single pass to avoid redirect chains
  const getRedirectDestination = (): string | null => {
    // Not authenticated - redirect to login
    if (!user) {
      return "/auth?mode=login";
    }

    // CRITICAL: Staff/managers must NEVER access customer-only routes
    // Check this BEFORE any other routing logic
    if (blockStaff && (isStaff || hasAccess)) {
      return "/dashboard";
    }

    // Also block staff from customer-only routes (allowedRoles = ["customer"] only)
    if (allowedRoles && allowedRoles.length === 1 && allowedRoles[0] === "customer") {
      if (isStaff || hasAccess) {
        return "/dashboard";
      }
    }

    // Email not verified - redirect to verification page
    const allowedVerificationPaths = ['/verify-email-pending', '/verify-email'];
    const isOnVerificationPath = allowedVerificationPaths.some(path => location.pathname.startsWith(path));
    
    if (profile && (!isEmailVerified || profile.email_verified === false)) {
      if (!isOnVerificationPath) {
        return "/verify-email-pending";
      }
    }

    // Business owner or staff check
    if (requireBusinessOwner) {
      if (allowStaff && isStaff) {
        return null; // Access granted
      }
      if (!isBusinessOwner) {
        if (role === "customer") return "/customer/bookings";
        if (role === "provider") return "/practitioner/schedule";
        return "/";
      }
    }

    // Role check
    if (allowedRoles && role && !allowedRoles.includes(role)) {
      if (allowStaff && isStaff) {
        return null; // Access granted
      }
      switch (role) {
        case "customer": return "/customer/bookings";
        case "solo":
        case "center_admin": return "/dashboard";
        case "provider": return "/practitioner/schedule";
        default: return "/";
      }
    }

    return null; // Access granted
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Calculate redirect destination once
  const redirectTo = getRedirectDestination();
  
  if (redirectTo) {
    // Add state for login redirect
    if (redirectTo === "/auth?mode=login") {
      return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Access granted
  return <>{children}</>;
}

// Specific route guards for common patterns
export function CustomerRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={["customer"]} blockStaff>
      {children}
    </ProtectedRoute>
  );
}

export function BusinessOwnerRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={["solo", "center_admin"]} requireBusinessOwner allowStaff>
      {children}
    </ProtectedRoute>
  );
}

export function CenterAdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={["center_admin"]}>
      {children}
    </ProtectedRoute>
  );
}

export function PractitionerRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={["provider"]}>
      {children}
    </ProtectedRoute>
  );
}
